import { useState, useEffect, useLayoutEffect, useMemo, } from "react"
import useLocalStorageState from "use-local-storage-state"
import { useSearchParams } from "react-router-dom"
import { Kanban } from "../../CommonComponents/kanban/Kanban"
import { IconCategoryFilled } from "@tabler/icons-react"
import DeleteModal from "../DeleteModal"
import { toast } from "sonner"
import { useGetRequest } from "../../Utils/hooks/QueryHandler"

import { DeleteRequest, PutRequest } from "../../Utils/hooks/ApiHandler"
import noDataFetched from "../../Utils/assets/img/noDataFetched.png"
import { CreatePipelinePage } from "../PipelineComponents/CreatePipelinePage"
import { Button } from "react-bootstrap"
import { getTodayDateValue, fetchTemporaryFilterData, } from "../../Utils/helpers/commonhelper"
import { AuthorizationCard } from "../../CommonComponents/AuthorizationCard"
import { EditPipelinePage } from "../PipelineComponents/EditPipelinePage"
// @ts-ignore
import { CreateLead } from "./CreateLead"
import { LeadsHeader } from "./LeadsHeader"
import useCommonData from "../../Utils/actions/advanceFilteringAction"
import Select from "react-select"
import { useGetEssentials } from "../../Utils/hooks/useGetEssentials"
import { useGlobalState } from "../../Utils/hooks/useFilterQueryValue"
import { useQueryClient } from "react-query"
import { useSharedPipelinePermission } from "../../Utils/hooks/usePipelineSharedPermission"

// Utility function for encoding pipeline IDs for URL parameters
const encodePipelineId = (id) => {
  const base64 = btoa(id.toString())
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")
}

const readLeadsMetadata = () => {
  try {
    return JSON.parse(localStorage.getItem("leadsMetadata") || "{}") || {}
  } catch {
    return {}
  }
}

const patchLeadsMetadata = (patch) => {
  localStorage.setItem(
    "leadsMetadata",
    JSON.stringify({
      ...readLeadsMetadata(),
      ...patch,
    })
  )
}

const getValidTakenFollowupFilter = (metadata) => {
  const saved = metadata?.takenFollowupFilter
  if (
    metadata?.filterType?.filterName === "Taken Followups" &&
    saved?.from &&
    saved?.to
  ) {
    return {
      from: saved.from,
      to: saved.to,
      ownerId: saved.ownerId ?? "",
    }
  }
  return null
}

export const Leads = () => {

  const { customer, setCustomer, headers, extras, navigate, customerId, loginUserId, userType } = useGetEssentials()

  // Session storage and authentication setup
  // parsing the leads metadata object
  const leadsMetadata = JSON.parse(localStorage.getItem('leadsMetadata'))
  // Loading State for delete
  const [loading, setLoading] = useState(false)

  // const location = useLocation()
  // const contactRedirectData = location?.state || null

  // variable for query params
  // getting the module name and id to fetch the respective module item's leads
  const [searchParams, setSearchParams] = useSearchParams()
  const moduleName = searchParams.get("module")
  const moduleId = searchParams.get("id")


  // State management for pipelines, stages, and leads
  const [pipelines, setPipelines] = useState([])
  const [selectedPipelineId, setSelectedPipelineId] = useState(() => {
    return sessionStorage.getItem("selectedLeadPipelineId") || null
  })
  const [pipelineStages, setPipelineStages] = useState([])
  const [leadsData, setLeadsData] = useState([])
  // Form modal states
  const [showCreateLead, setShowCreateLead] = useState(false)
  const [showLeadDetails, setShowLeadDetails] = useState(false)
  const [selectedLeadId, setSelectedLeadId] = useState(null)
  const [editLeadId, setEditLeadId] = useState(null)
  const [selectedStageId, setSelectedStageId] = useState(null)
  // Delete confirmation states
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteLeadId, setDeleteLeadId] = useState(null)
  const [showPipelineDeleteModal, setShowPipelineDeleteModal] = useState(false)
  const [pipelineToDelete, setPipelineToDelete] = useState(null)

  // for storing the parsed shared users to pass down to the pipeline share model where i will render these users with their respective permissions corresponding to that pipeline
  const [parsedSharedUsers, setParsedSharedUsers] = useState(null)

  // state for force fetching the taken followup
  const [appliedTakenFollowupFilter, setAppliedTakenFollowupFilters] = useState(() =>
    getValidTakenFollowupFilter(leadsMetadata)
  )

  // Search states for debounced search
  const [debouncedSearch, setDebouncedSearch] = useState("")

  const [editPipeline, setEditPipeline] = useState(false)
  const [encodedId, setEncodedId] = useState(null)

  const [createPipeline, setCreatePipeline] = useState(false)
  // Pagination states for list view
  const [limit, setLimit] = useState(25)
  const [listOffset, setListOffset] = useState(0)
  const [cardOffset, setCardOffset] = useState(0)

  // Local storage for view settings
  const [currView, setCurrView] = useState(() => {
    return leadsMetadata?.currView || "card"
  })
  const activeOffset = currView === "card" ? cardOffset : listOffset
  const setActiveOffset =
    currView === "card" ? setCardOffset : setListOffset

  // Tab focus tracking for handling tab switches
  const [isTabVisible, setIsTabVisible] = useState(true)
  const [forceRefetch, setForceRefetch] = useState(0)

  // states for taken followups filter
  const [showCustomInputs, setShowCustomInputs] = useState(false);
  const [customFrom, setCustomFrom] = useState(() => {
    const saved = getValidTakenFollowupFilter(leadsMetadata)
    return saved?.from || getTodayDateValue()
  });
  const [customTo, setCustomTo] = useState(() => {
    const saved = getValidTakenFollowupFilter(leadsMetadata)
    return saved?.to || getTodayDateValue()
  });

  // owner dropdown options for taken followups in filter cards
  const [selectedCustomOwner, setSelectedCustomOwner] = useState(() => {
    const saved = getValidTakenFollowupFilter(leadsMetadata)
    if (saved?.ownerId === "all") {
      return { value: "all", label: "All" }
    }
    return null
  });

  // Permanent fields that are always visible in the table
  const permanentFields = [
    "contact_full_name",
    "contact_phone",
    "email",
    "requirement",
    "budget",
    "lead_status",
    "pipelineStageName",
    "source_title"
  ]

  // // Available lead fields for column configuration (excluding permanent fields)
  const columnOptions = [
    { id: "name", label: "Name", mandatory: true },
    { id: "contact_phone", label: "Phone" },
    { id: "email", label: "Email" },
    { id: "requirement", label: "Requirement" },
    { id: "pipelineStageName", label: "Pipeline Stage Name" },
    { id: "lead_status", label: "Lead Status" },
    { id: "budget", label: "Budget" },
    { id: "source_title", label: "Source" },
    { id: "expected_closure_date", label: "Expected Closure Date" },
    { id: "owner", label: "Lead Owner" },
    { id: "followup_taken_by", label: "Followup Taken By" },
    { id: "followup_created_on", label: "Followup Created On" },
    { id: "created_on", label: "Created On" },
  ]


  // Initialize columns for the table - ALL columns visible by default
  const [columns, setColumns] = useState(() => {
    const saved = leadsMetadata?.visibleColumns;
    const allColumns = columnOptions.map(col => col.label);
    if (saved) {
      try {
        return saved;
      } catch (e) {
        console.error("Error parsing visible columns:", e);
      }
    }
    // Default: all columns visible
    return allColumns;
  });

  // Filter states
  const [filterStates, setFilterState] = useState([])
  const [filteredLeads, setFilteredLeads] = useState(null)

  const [heading, setHeading] = useState("All Leads");


  // loading state for temporary filter data to be hydrated
  const [tempFilterDataLoading, setTempFilterDataLoading] = useState(false)

  // global state to manage the apply filter data parameters and current filtered state
  const [leadsApplyFilterParams, setLeadsApplyFilterParams] = useGlobalState('leadsApplyFilterDataParams', { currAdvFilter: null, applyFilterData: {} })

  // curr advance filter to store the current filter type between "apply" and "save as" in advance filter for respective pagination in apply filter
  const [currAdvFilter, setCurrAdvFilter] = useState(leadsApplyFilterParams?.['currAdvFilter'] || null)
  // state for filter dropdown of quotations
  const [filterDropdown, setFilterDropdown] = useState(false);
  // global value state for applying the pagination to temporary filter data
  const [leadsFilterValues, setLeadsFilterValues] = useGlobalState("leadsApplyFilterQueryValues")

  // filter categories to render within the heading dropdown
  const [filterCategories, setFilterCategories] = useState([])
  // state to storethe advanced custom filter to fetch data on save
  const [customFilter, setCustomFilter] = useState(null)

  // for storing the pipeline data to pass down to the advance filtering component
  const [filterDropPipelineStage, setFilterDropPipelineStage] = useState([])

  // filter state to see the temoporary applied filter data,
  const [tempFilterData, setTempFilterData] = useState(null);

  // checking and validating if the last selected filter still exist until then the state initialized would be false and no data or the heading would be renderd
  const [isInitialized, setIsInitialized] = useState(false);

  // permission setup for leads
  const [PERMISSION, _] = useLocalStorageState('permission')
  const leadGrant = PERMISSION?.leads;
  const leadPipelineGrant = leadGrant.feature.pipeline;

  // we are declaring params here to ensure that the apply filter data gets fetched 
  const applyFilterDataParams = {
    filterValues: leadsFilterValues,
    selectedPipelineId: selectedPipelineId,
    currView: currView,
    offset: activeOffset,
    limit: limit,
    module_name: "lead",
    customerId: customerId,
    loginUserId: loginUserId,
    userType: userType,
    headers: headers,
    extras: extras,
    setTempFilterData: setTempFilterData,
    setLoading: setTempFilterDataLoading,
    setFilterState: setFilterState,
    setFilterDropdown: setFilterDropdown
  }

  useEffect(() => {

    setLeadsApplyFilterParams({
      currAdvFilter: currAdvFilter,
      applyFilterData: {
        ...applyFilterDataParams,
        filterValues: leadsFilterValues,
        currView: currView,
        selectedPipelineId: selectedPipelineId,
        offset: activeOffset,
        limit: limit
      }
    })
  }, [currAdvFilter, activeOffset, limit, currView, selectedPipelineId, leadsFilterValues,])

  // Stored apply params still describe the previous view until the effect above
  // writes them. Hold the list skeleton until that view's payload arrives.
  useLayoutEffect(() => {
    if (currAdvFilter !== "apply") return
    if (!pipelines.length) return
    if (!leadsFilterValues?.["filter_values"]?.length) return
    const stored = leadsApplyFilterParams?.applyFilterData
    const storedViewMatches =
      stored?.currView === currView &&
      stored?.offset === activeOffset &&
      stored?.limit === limit
    if (!storedViewMatches) {
      setTempFilterDataLoading(true)
    }
  }, [currAdvFilter, currView, activeOffset, limit, leadsApplyFilterParams, leadsFilterValues, pipelines.length])
  useEffect(() => {
    localStorage.setItem('leadsMetadata', JSON.stringify({
      ...leadsMetadata,
      currView: currView
    }))
  }, [currView])

  useEffect(() => {
    localStorage.setItem('leadsMetadata', JSON.stringify({
      ...leadsMetadata,
      currView: currView
    }))
  }, [currView])

  // common advance filter api calls that will be needed everywhere
  const { leadOwner, lead_source, company, contacts } = useCommonData({
    leadOwner: true,
    leadSource: true,
    companies: true,
    contacts: true
  })

  useEffect(() => {
    if (!isInitialized) {
      const lastHeading = leadsMetadata?.filterType?.filterName
      const validTakenFollowup = getValidTakenFollowupFilter(leadsMetadata)

      if (lastHeading === "Taken Followups" && !validTakenFollowup) {
        setHeading("All Leads")
        patchLeadsMetadata({
          filterType: { filterName: "All Leads", id: null },
          takenFollowupFilter: null,
        })
      } else {
        setHeading(lastHeading || "All Leads")
      }
      setIsInitialized(true)
    }
  }, [isInitialized, leadsMetadata])

  // Restore Taken Followups owner option once owner list is available
  useEffect(() => {
    const ownerId = appliedTakenFollowupFilter?.ownerId
    if (ownerId == null || ownerId === "") return
    if (ownerId === "all") {
      setSelectedCustomOwner({ value: "all", label: "All" })
      return
    }
    if (!leadOwner?.length) return
    const match = leadOwner.find((owner) => String(owner.value) === String(ownerId))
    if (match) {
      setSelectedCustomOwner(match)
    }
  }, [leadOwner, appliedTakenFollowupFilter?.ownerId])

  // Clear Taken Followups params when the user leaves that filter
  useEffect(() => {
    if (!isInitialized) return
    if (heading === "Taken Followups") return

    const current = readLeadsMetadata()
    if (current.takenFollowupFilter != null) {
      patchLeadsMetadata({ takenFollowupFilter: null })
    }

    setAppliedTakenFollowupFilters((prev) => (prev == null ? prev : null))
    setCustomFrom(getTodayDateValue())
    setCustomTo(getTodayDateValue())
    setSelectedCustomOwner(null)
  }, [heading, isInitialized])


  // Fetch all pipelines for the customer
  const {
    data: pipelineData,
    isLoading: isPipelineLoading,
    isError: pipelineDataError,
    refetch: refetchPipeline,
    status: pipelineFetchStatus
  } = useGetRequest(
    `lead/fetchPipelines/${customerId}/leads/${loginUserId}/${userType}`,
    ["fetchLeadsPipeline", forceRefetch],
    headers,
    extras,
    {}
  )


  useEffect(() => {
    if (selectedPipelineId !== null && selectedPipelineId !== undefined) {
      sessionStorage.setItem("selectedLeadPipelineId", String(selectedPipelineId))
    }
  }, [selectedPipelineId])

  // Handle pipeline data updates and set default selection
  useEffect(() => {
    if (pipelineFetchStatus === "success" && pipelineData) {
      const fetchedPipelines = pipelineData.data || []
      setPipelines(fetchedPipelines)

      const savedId = sessionStorage.getItem("selectedLeadPipelineId")
      const savedExists = fetchedPipelines.some(
        (p) => String(p.id) === String(savedId)
      )

      if (savedExists) {
        setSelectedPipelineId(savedId)
      } else if (fetchedPipelines.length > 0) {
        const selectpipe =
          fetchedPipelines.find((el) => el.is_active === "yes") ||
          fetchedPipelines[0]

        setSelectedPipelineId(selectpipe?.id ?? null)

        if (selectpipe?.id != null) {
          sessionStorage.setItem("selectedLeadPipelineId", String(selectpipe.id))
        }
      } else {
        setSelectedPipelineId(null)
      }
    } else {
      setPipelines([])
      setSelectedPipelineId(null)
    }
  }, [pipelineData, pipelineFetchStatus])

  const pipelineStageUrl = selectedPipelineId ? `lead/fetchPipelineStage/${selectedPipelineId}/${customerId}` : null

  // Fetch pipeline stages for selected pipeline
  const {
    data: pipelineStageData,
    isLoading: isPipelineStageLoading,
    isError: stageLoadingError,
    refetch: refetchPipelineStage,
    status: stageFetchStatus
  } = useGetRequest(
    pipelineStageUrl,
    ["fetchPipelineStage", selectedPipelineId],
    headers,
    extras,
    // {
    //   enabled: !!selectedPipelineId && isTabVisible,
    // }
  )

  // Handle pipeline stage data updates and formatting
  useEffect(() => {
    if (stageFetchStatus === "success" && pipelineStageData) {
      const formattedStages = pipelineStageData.data.map((stage) => ({
        ...stage,
        stage_id: stage.id,
        sequence: String(stage.sequence),
        probability: Math.min(100, stage.probability || 0),
        rotting_days: stage.rotting_days === null ? null : stage.rotting_days,
        rotting_enabled: stage.rotting_days !== null && stage.rotting_days > 0,
      }))
      const filterDropdownData = pipelineStageData.data.map((c) => ({
        value: c.id,
        label: c.stage_name
      }))
      setPipelineStages(formattedStages)
      setSelectedStageId(formattedStages[0]?.id)
      setFilterDropPipelineStage(filterDropdownData)
    } else {
      setPipelineStages([])
    }
  }, [pipelineStageData, heading])

  // Create params object
  const leadsParams = {}
  if (currView === "list") {
    leadsParams.limit = limit
    leadsParams.offset = activeOffset
  }
  if (debouncedSearch) {
    leadsParams.search = debouncedSearch
  }

  const { endpoint, queryKey } = useMemo(() => {
    console.log('currAdvFilter', currAdvFilter)
    if (!selectedPipelineId || currAdvFilter === 'apply') {
      return {
        endpoint: "",
        queryKey: "",
      }
    }
    // console.log('entered')
    // resetting state if currAdvFilter is null
    setLeadsFilterValues({ filter_state: [], filter_values: [] })
    const customFilterId = leadsMetadata?.filterType?.id

    const lowerHeading = heading
      ? heading.toLowerCase().split(" ").join("_")
      : heading

    if (!customFilter && !customFilterId) {
      if (lowerHeading === "leads_created_by_me") {
        return {
          endpoint: `lead/leadsCreatedByMe/${loginUserId}/${customerId}/${selectedPipelineId}/${userType}/${currView}`,
          queryKey: "leadsCreatedByMe",
        }
      }

      if (lowerHeading === "leads_created_today") {
        return {
          endpoint: `lead/getLeadsCreatedBasedOnDuration/${customerId}/today/${selectedPipelineId}/${loginUserId}/${userType}/${currView}`,
          queryKey: "leadsCreatedToday",
        }
      }

      if (lowerHeading === "leads_created_this_month") {
        return {
          endpoint: `lead/getLeadsCreatedBasedOnDuration/${customerId}/month/${selectedPipelineId}/${loginUserId}/${userType}/${currView}`,
          queryKey: "leadsCreatedThisMonth",
        }
      }

      if (lowerHeading === "my_today_followup") {
        return {
          endpoint: `lead/fetchLeadsBasedOnTodaysFollowup/${customerId}/${selectedPipelineId}/${loginUserId}/${userType}/${currView}/filter_card_id=null`,
          queryKey: "mytodayfollowup",
        }
      }

      if (lowerHeading === "taken_followups") {
        return {
          endpoint: `lead/fetchTakenFollowups/${customerId}/${selectedPipelineId}/${loginUserId}/${userType}/${currView}/filter_card_id=null?from=${appliedTakenFollowupFilter?.from}&to=${appliedTakenFollowupFilter?.to}&owner_id=${appliedTakenFollowupFilter?.ownerId || ""}`,
          queryKey: "takenfollowup",
        }
      }

      if (lowerHeading === "won_leads") {
        return {
          endpoint: `lead/getLeadsBasedOnStatus/${customerId}/${selectedPipelineId}/won/${loginUserId}/${userType}/${currView}/filter_card_id=null`,
          queryKey: "fetchLostLeads",
        }
      }
      if (lowerHeading === "lost_leads") {
        return {
          endpoint: `lead/getLeadsBasedOnStatus/${customerId}/${selectedPipelineId}/lost/${loginUserId}/${userType}/${currView}/filter_card_id=null`,
          queryKey: "fetchLostLeads",
        }
      }

      if (lowerHeading === "unqualified_leads") {
        return {
          endpoint: `lead/getLeadsBasedOnStatus/${customerId}/${selectedPipelineId}/unqualified/${loginUserId}/${userType}/${currView}/filter_card_id=null`,
          queryKey: "fetchUnqualifiedLeads",
        }
      }

      if (lowerHeading === "all_leads") {
        if (!moduleName) {
          return {
            endpoint: `lead/fetchLeads/${customerId}/${selectedPipelineId}/${loginUserId}/${userType}/${currView}`,
            queryKey: "fetchAllLeads",
          }
        }
        return {
          endpoint: `lead/fetchLeads/${customerId}/${selectedPipelineId}/${loginUserId}/${userType}/${currView}?module=${moduleName}&id=${moduleId}`,
          queryKey: moduleName === "contact" ? "fetchContactLeads" : 'fetchCompanyLeads'
        }
      }
    }
    if (customFilter || customFilterId) {

      const id = customFilter?.id || customFilterId
      return {
        endpoint: `lead/fetchLeads/${customerId}/${selectedPipelineId}/${loginUserId}/${userType}/${currView}/${id}`,
        queryKey: `filterQuery${id}`,
      }
    }
    return {
      endpoint: "",
      queryKey: "",
    }
  }, [
    heading,
    filterCategories,
    selectedPipelineId,
    currView,
    appliedTakenFollowupFilter,
    customFilter,
    currAdvFilter
  ])
  console.log('endpoint', endpoint, 'queryKey', queryKey)
  // leads fetching
  const {
    data: leadsApiData,
    isLoading: isLeadsLoading,
    isPreviousData: isLeadsPreviousData,
    isError: leadsLoadError,
    refetch: refetchLeads,
  } = useGetRequest(
    endpoint,
    [queryKey,
      selectedPipelineId,
      forceRefetch,
      activeOffset,
      appliedTakenFollowupFilter
      // limit,
      // debouncedSearch
    ],
    headers,
    { customer, setCustomer, rf: headers.Authentication, navigate },
    {
      ...leadsParams,
      enabled: !!selectedPipelineId && isTabVisible && currView
    }
  );

  // keepPreviousData can leave card-shaped stages in the query result while list
  // is fetching. Do not sync that stale payload into list rows; show TableWrapper skeleton.
  useLayoutEffect(() => {
    if (isLeadsPreviousData) {
      if (currView === "list") {
        setLeadsData([])
      }
      return
    }

    const sharedUsers = leadsApiData?.pipeline_data?.["shared_to"]
    setParsedSharedUsers(JSON.parse(sharedUsers !== undefined && sharedUsers) || [])

    setLeadsData(
      leadsApiData?.data?.map(stage => ({
        ...stage,
        leads: stage.leads?.map(lead => ({
          ...lead,
          product_services:
            // typeof lead.product_services === "string"
            //   ? (() => {
            //     try {
            //       return lead.product_services
            //     } catch {
            // return
            lead.product_services
          //   }
          // })()
          // : lead.product_services
        }))
      }))
    );
  }, [leadsApiData, queryKey, isLeadsPreviousData, currView])

  // // hook for getting the permissions assigned to the user
  // const { permissionAssigned, canEdit_canDownload } = usePermissions(customer, parsedSharedUsers, leadsApiData?.pipeline_data?.['added_by'])

  // fetching shared pipeline permissions for the current selected pipline
  const { permissionAssigned, canEdit } = useSharedPipelinePermission(
    selectedPipelineId,
    customerId,
    loginUserId,
    userType,
    headers,
    extras,
    'leads')


  // Update lead stage with optimistic UI updates
  const handleUpdateLead = async (leadData, newStageId, oldStageId) => {
    const originalLeadsData = [...leadsData]

    // Optimistically update UI
    setLeadsData((prev) => {
      const newData = [...prev]

      const oldStageIndex = newData.findIndex((stage) => stage.stage_id === oldStageId)
      if (oldStageIndex !== -1) {
        newData[oldStageIndex] = {
          ...newData[oldStageIndex],
          leads: newData[oldStageIndex].leads.filter((lead) => lead.lead_id !== leadData.lead_id),
        }
      }

      const newStageIndex = newData.findIndex((stage) => stage.stage_id === newStageId)
      if (newStageIndex !== -1) {
        newData[newStageIndex] = {
          ...newData[newStageIndex],
          leads: [...newData[newStageIndex].leads, leadData],
        }
      }

      return newData
    })

    const selectedPipeline = pipelines.find((p) => p.id === selectedPipelineId)
    const updatePayload = {
      customer_id: customerId,
      user_login_id: loginUserId,
      owner_id: leadData.owner_id,
      lead_id: leadData.lead_id,
      contact_id: leadData.contact_id,
      pipeline_id: selectedPipeline.id,
      pipeline_stage_id: newStageId,
      expected_closure_date: leadData.expected_closure_date,
      expected_closure_time: leadData.expected_closure_time,
      requirement: leadData.requirement,
      product_services: JSON.parse(leadData.product_services),
      budget: leadData.budget,
      lead_source_id: leadData.lead_source_id,
    }
    console.log('updatePayload', updatePayload)
    try {
      const response = await PutRequest(`lead/updateLead/`, updatePayload, { headers }, extras)

      if (response?.status === "success") {
        toast.success(response.message || "Lead updated successfully")
        if (currAdvFilter === "apply") {
          fetchTemporaryFilterData(applyFilterDataParams)
          return
        }
        setCurrAdvFilter(null)
        setFilterState([])
        setTempFilterData(null)
        setCustomFilter(null)
        setLeadsFilterValues({ filter_values: [], filter_state: [] })
        refetchLeads()
        await refetchLeads()
      } else {
        toast.error(response?.message || "Failed to update lead")
        setLeadsData(originalLeadsData)
      }
    } catch (error) {
      toast.error(error.message || "Failed to save lead")
      setLeadsData(originalLeadsData)
    }
  }

  // Confirm and execute pipeline deletion
  const handlePipelineDeleteConfirm = async () => {
    if (!pipelineToDelete) return

    try {
      setLoading(true)
      const response = await DeleteRequest(`lead/deletePipeline/${pipelineToDelete}/${customerId}/${loginUserId}/leads`, { headers }, extras)
      if (response?.status === "success") {
        toast.success("Pipeline deleted successfully")
        setShowPipelineDeleteModal(false)
        setPipelineToDelete(null)
        setLoading(false)
        await refetchPipeline()
        navigate(`/leads`)
      } else {
        toast.error(response?.message || "Failed to delete pipeline")
        setLoading(false)
      }
    } catch (error) {
      toast.error("An error occurred while deleting pipeline")
      setLoading(false)
    }
  }
  // Open add lead form
  const handleAddLead = (stageId = null) => {
    if (!leadGrant?.isCreate) {
      toast.warning("You are not authorised for this action!")
      return
    }
    if (!canEdit) {
      toast.warning("You are not a shared pipeline user. You are not authorized for this action!")
      return
    }
    setSelectedStageId(stageId)
    setEditLeadId(null)
    setShowCreateLead(true)
  }

  // Open edit lead form with specific lead ID
  const handleEditLead = (leadId) => {
    if (!leadGrant.isEdit) {
      toast.warning("You are not authorized for this action!")
      return
    }
    setEditLeadId(leadId)
    setSelectedStageId(null)
    setShowCreateLead(true)
  }

  // Show delete confirmation modal for lead
  const handleDeleteLead = (leadId) => {
    if (!leadGrant.isDelete) {
      toast.warning("You are not authorized for this action!")
      return
    }
    setDeleteLeadId(leadId)
    setShowDeleteModal(true)
  }

  // Open lead details modal with proper lead ID
  const handleOpenLeadDetails = (lead) => {
    setShowLeadDetails(false)
    setSelectedLeadId(null)
    setTimeout(() => {
      setSelectedLeadId(lead.lead_id)
      setShowLeadDetails(true)
    }, 50)
  }

  // Close lead details and refresh data
  // const handleCloseLeadDetails = () => {
  //   setShowLeadDetails(false);
  //   setSelectedLeadId(null);
  //   queryClient.removeQueries("fetchLeadDetails")
  // };

  // Confirm and execute lead deletion
  const handleDeleteConfirm = async () => {
    try {
      setLoading(true)
      const response = await DeleteRequest(`lead/deleteLead/${deleteLeadId}/${loginUserId}`, { headers }, extras)
      if (response.status === "fail" || response.success === false) {
        toast.error(response.message)
        setLoading(false)
        return
      }
      toast.success(response.message)
      setLoading(false)
      if (currAdvFilter === "apply") {
        fetchTemporaryFilterData(applyFilterDataParams)
        return
      }
      setCurrAdvFilter(null)
      setFilterState([])
      setTempFilterData(null)
      setCustomFilter(null)
      refetchLeads()

    } catch (error) {
      toast.error("An error occurred while deleting lead")
      setLoading(false)
    } finally {
      setShowDeleteModal(false)
      setDeleteLeadId(null)
      setLoading(false)
    }
  }

  // Close delete confirmation modal
  const handleDeleteClose = () => {
    setShowDeleteModal(false)
    setDeleteLeadId(null)
    setLoading(false)
  }

  // Close create lead form and reset selection
  const handleCreateLeadClose = () => {
    setShowCreateLead(false)
    setSelectedStageId(null)
  }

  // Pagination handlers
  const handlePageChange = (selectedPage) => {
    setActiveOffset(selectedPage.selected)
  }

  // customized filter cards for filtering data. These cards are supposed to be shown in filter cards dropdown
  const customFilterCards = (closeDropdown) => {
    return <div className="px-3 py-2">
      <div
        role="button"
        style={{ cursor: 'pointer', fontSize: '14px' }}
        className="d-flex gap-2 align-items-center ps-1"
        onClick={(e) => {
          e.stopPropagation();
          setShowCustomInputs(prev => !prev);
        }}
      >
        <IconCategoryFilled size={15} />
        Taken FollowUps
      </div>

      {showCustomInputs && (
        <div
          className="d-flex flex-column gap-2 mt-2"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="d-flex gap-2 mt-2">
            <input
              type="date"
              className="form-control"
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
            />

            <input
              type="date"
              className="form-control"
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
            />
          </div>
          <div className="d-flex flex-column gap-2 mt-2">
            <div>
              <Select
                options={[
                  ...(leadOwner || []),
                  {
                    value: 'all', label: "All"
                  }
                ]}
                value={selectedCustomOwner}
                onChange={(selectedOption) => {
                  setSelectedCustomOwner(selectedOption || null);
                }}
                isSearchable
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: "38px",
                    fontSize: "14px",
                  }),
                  placeholder: (provided) => ({
                    ...provided,
                    paddingLeft: "11px",
                  }),
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 99999,
                  }),
                }}
                placeholder="Select Owner"
                menuPortalTarget={document.body}
                menuPosition="fixed"
                menuShouldBlockScroll={true}
                captureMenuScroll={true}
              />
            </div>

          </div>
          <div className="d-flex align-items-center w-100 justify-content-end gap-2">
            <button
              className="btn btn-sm btn-default-outline w-50"
              onClick={async (e) => {
                e.stopPropagation();
                setShowCustomInputs(false)
                setCustomFrom(getTodayDateValue())
                setCustomTo(getTodayDateValue())
                setSelectedCustomOwner(null)
                setAppliedTakenFollowupFilters(null)
                setHeading("All Leads")
                patchLeadsMetadata({
                  filterType: { filterName: "All Leads", id: null },
                  takenFollowupFilter: null,
                })
              }}
            >
              Cancel
            </button>
            <button
              className="btn btn-sm btn-default w-50"
              onClick={async (e) => {
                e.stopPropagation();
                const fromDate = customFrom || getTodayDateValue();
                const toDate = customTo || getTodayDateValue();
                const nextFilter = {
                  from: fromDate,
                  to: toDate,
                  ownerId: selectedCustomOwner?.value || "",
                }

                setHeading("Taken Followups");
                setAppliedTakenFollowupFilters(nextFilter);
                patchLeadsMetadata({
                  filterType: { filterName: "Taken Followups", id: null },
                  takenFollowupFilter: nextFilter,
                })
                setShowCustomInputs(false);
                closeDropdown?.();
              }}
            >
              Submit
            </button>
          </div>
        </div>
      )}
    </div>
  }

  // const extracting the selected pipeline data
  const selectedPipeline = pipelines.find((p) => p.id === selectedPipelineId)

  // isLoading will depend on all the loading stages when they are true and the rest of the error states are false.
  const isLoading =
    isPipelineLoading ||
    isPipelineStageLoading ||
    isLeadsLoading


  // if there's some error while fetching the data then we will show a retry ui to the user
  const isErrorFetching =
    pipelineDataError || stageLoadingError || leadsLoadError

  if (editPipeline && encodedId) {
    return (
      <EditPipelinePage
        encodedId={encodedId}
        module_name="lead"
        refetch={refetchPipeline}
        refetchPipelineStage={refetchPipelineStage}
        setEditPipeline={setEditPipeline}
        pipelineCount={pipelines.length}
        pipelineGrant={leadPipelineGrant}
      />
    )
  }

  if (createPipeline) {
    return (
      <CreatePipelinePage
        module_name="lead"
        refetchPipelineData={refetchPipeline}
        setCreatePipeline={setCreatePipeline}
        setSelectedPipelineId={setSelectedPipelineId}
        pipelineGrant={PERMISSION?.leads?.feature?.pipeline}
      />
    )
  }

  // if no permission for module then authorization card
  if (!leadGrant.isView) {
    return <div style={{ position: "absolute", left: "40%", top: "10rem" }}>
      <AuthorizationCard />
    </div>
  }
  return (
    <>
      {/* Enhanced loading state with better UX */}
      {
        isLoading && (
          <div className="d-flex justify-content-center align-items-center" style={{ height: "90vh" }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )
      }

      {isErrorFetching &&
        < div
          className="d-flex flex-column justify-content-center align-items-center"
          style={{ height: "90vh" }}
        >
          <img src={noDataFetched} style={{ width: '640px' }} alt="no_data_found" />
          <h4 className="mt-3">Couldn't load your leads</h4>

          <p
            className="text-muted text-center"
            style={{ maxWidth: 450 }}
          >
            Something went wrong while loading the data.
            Please check your internet connection and try again.
          </p>

          <button
            className="btn btn-default mt-2"
            onClick={() => {
              setCurrAdvFilter(null)
              setFilterState([])
              setTempFilterData(null)
              setCustomFilter(null)
              setLeadsFilterValues({ filter_values: [], filter_state: [] })
              refetchPipeline();
              refetchPipelineStage();
              refetchLeads();
            }}
          >
            Retry
          </button>
        </div >
      }
      {
        !isLoading && !isErrorFetching && !tempFilterDataLoading && pipelines.length === 0 &&
        < div
          className="d-flex flex-column justify-content-center align-items-center"
          style={{ height: "90vh" }}
        >
          <img src={noDataFetched} style={{ width: '640px' }} alt="no_data_found" />
          <h4 className="mt-3">No Pipelines Found</h4>

          <p
            className="text-muted text-center "
          >
            You haven't created any pipeline yet.
          </p>

          <Button
            onClick={() => {
              if (!leadPipelineGrant?.isCreate) {
                toast.warning('You are not authorized for this action!')
                return
              }
              setCreatePipeline(true)
            }}
            className="btn btn-default btn-sm mt-2"
          >
            Create Your first Pipeline
          </Button>
        </div>
      }

      {/* header component for leads */}
      {
        !isLoading && !isErrorFetching && pipelines.length > 0 &&
        <LeadsHeader
          heading={heading}
          setHeading={setHeading}
          permissionAssigned={permissionAssigned}
          canEdit_canDownload={canEdit}
          filterCategories={filterCategories}
          setFilterCategories={setFilterCategories}
          filterDropPipelineStage={filterDropPipelineStage}
          leadData={leadsData}
          filterDropdown={filterDropdown}
          setFilterDropdown={setFilterDropdown}
          pipelines={pipelines}
          selectedPipelineId={selectedPipelineId}
          setSelectedPipelineId={setSelectedPipelineId}
          setDebouncedSearch={setDebouncedSearch}
          currView={currView}
          setCurrView={setCurrView}
          // leadGrant={leadGrant}
          // leadPipelineGrant={leadPipelineGrant}
          setCreatePipeline={setCreatePipeline}
          encodePipelineId={encodePipelineId}
          handleAddLead={handleAddLead}
          setEditPipeline={setEditPipeline}
          setActiveOffset={setActiveOffset}
          setEncodedId={setEncodedId}
          selectedPipeline={selectedPipeline}
          parsedSharedUsers={parsedSharedUsers}
          selectedStageId={selectedStageId}
          filterStates={filterStates}
          setFilterState={setFilterState}
          leadsApiData={leadsApiData}
          queryKey={queryKey}
          setTempFilterData={setTempFilterData}
          currAdvFilter={currAdvFilter}
          setCurrAdvFilter={setCurrAdvFilter}
          refetchLeads={refetchLeads}
          columns={columns}
          setColumns={setColumns}
          columnOptions={columnOptions}
          customFilterCards={customFilterCards}
          contacts={contacts}
          company={company}
          leadOwner={leadOwner}
          lead_source={lead_source}
          setCustomFilter={setCustomFilter}
          leadsMetadata={leadsMetadata}
          filterValues={leadsFilterValues?.filter_state}
          setLeadsFilterValues={setLeadsFilterValues}
        />
      }
      {/* Kanban board - Enhanced rendering logic */}
      {
        !isLoading && !isErrorFetching && pipelines.length !== 0 && (
          <Kanban
            stages={pipelineStages}
            canEdit_canDownload={canEdit}
            data={leadsData}
            setData={setLeadsData}
            onUpdateLead={handleUpdateLead}
            onRefetchLeads={refetchLeads}
            pipelineName={selectedPipeline?.pipeline_name || null}
            onAddLead={handleAddLead}
            onEditLead={handleEditLead}
            // onFollowUpLead={handleFollowUpLead}
            onDeleteLead={handleDeleteLead}
            currView={currView}
            visibleColumns={columns}
            filteredLeads={filteredLeads}
            onOpenLeadDetails={handleOpenLeadDetails}
            totalModuleDataCount={leadsApiData?.total_count}
            offset={activeOffset}
            limit={limit}
            // pageCount={pageCount}
            handlePageChange={handlePageChange}
            setOffset={setActiveOffset}
            setLimit={setLimit}
            mainGrant={leadGrant}
            tempFilterData={tempFilterData}
            followupGrant={leadGrant.feature.followup}
            headers={headers}
            extras={extras}
            module_name={'lead'}
            isDataLoading={
              isLeadsLoading ||
              // keepPreviousData stays isPreviousData while apply disables listing GET (endpoint "")
              (currView === "list" && isLeadsPreviousData && currAdvFilter !== "apply") ||
              tempFilterDataLoading
            }
            pipelineStage={filterDropPipelineStage}
            pipeline_id={selectedPipelineId}
            refetchData={refetchLeads}
            currAdvFilter={currAdvFilter}
            setFilterState={setFilterState}
            setFilterDropdown={setFilterDropdown}
            filterValues={leadsFilterValues}
            setTempFilterData={setTempFilterData}
            applyFilterDataParams={leadsApplyFilterParams}
            tempFilterDataLoading={tempFilterDataLoading}
          />
        )
      }
      {/* Create/Edit Lead Modal */}
      {
        <CreateLead
          show={showCreateLead}
          onHide={handleCreateLeadClose}
          setLeadClose={setShowCreateLead}
          editId={editLeadId}
          stageId={selectedStageId}
          pipelineId={selectedPipelineId}
          refetchLeads={refetchLeads}
          module_name={'leads'}
          currAdvFilter={currAdvFilter}
          applyFilterDataParams={leadsApplyFilterParams}
          setCurrAdvFilter={setCurrAdvFilter}
          setFilterState={setFilterState}
          setTempFilterData={setTempFilterData}
          setCustomFilter={setCustomFilter}
          setLeadsFilterValues={setLeadsFilterValues}
        />

      }
      {/* {
        selectedLeadId &&
        <LeadsDetails
          show={showLeadDetails}
          canEdit={canEdit}
          permissionAssigned={permissionAssigned}
          refetch={refetchLeads}
          onClose={handleCloseLeadDetails}
          onDelete={handleDeleteLead}
          // onFollowUp={handleFollowUpLead}
          leadId={selectedLeadId}
          pipelineStageData={pipelineStages}
          currentPipelineId={selectedPipelineId}
          currAdvFilter={currAdvFilter}
          applyFilterDataParams={applyFilterDataParams}
          setCurrAdvFilter={setCurrAdvFilter}
          setFilterState={setFilterState}
          setTempFilterData={setTempFilterData}
          setCustomFilter={setCustomFilter}
          setLeadsFilterValues={setLeadsFilterValues}
        />
      } */}

      {/* Delete Lead Confirmation Modal */}
      <DeleteModal
        show={showDeleteModal}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
        isLoading={loading}
        message="Are you sure you want to delete this lead?"
      />

      {/* Pipeline Delete Confirmation Modal */}
      <DeleteModal
        show={showPipelineDeleteModal}
        onClose={() => {
          setShowPipelineDeleteModal(false)
          setPipelineToDelete(null)
        }}
        onConfirm={handlePipelineDeleteConfirm}
        isLoading={loading}
        message="Are you sure you want to permanently delete this pipeline?"
      />

    </>
  )
}

export default Leads